'use client';

import { GetProfessionalActivitiesStatusFilter } from '@api/models';
import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useLocale } from 'next-intl';

import { Activity, getActivitiesByProfessional } from '../services/activities.service';

export const useActivitiesByProfessionalId = (
  professionalId: string | undefined,
  currentActivityId: string,
  statusFilter?: GetProfessionalActivitiesStatusFilter
) => {
  const locale = useLocale();

  return useQuery({
    queryKey: ['activities', 'professional', professionalId, currentActivityId, locale, statusFilter],
    queryFn: async () => {
      if (!professionalId) {
        return [];
      }

      const activities = await getActivitiesByProfessional(professionalId, { locale, statusFilter });
      // Filter out the current activity to avoid showing it in the "More from this professional" section
      return activities.filter((activity: Activity) => activity.id !== currentActivityId);
    },
    enabled: !!professionalId
  });
};

/**
 * Suspense-enabled version of useActivitiesByProfessionalId.
 * Use this with React Suspense boundaries for cleaner loading states.
 * Note: professionalId must be provided (not undefined) for Suspense to work correctly.
 */
export const useActivitiesByProfessionalIdSuspense = (
  professionalId: string,
  currentActivityId: string,
  statusFilter?: GetProfessionalActivitiesStatusFilter
) => {
  const locale = useLocale();

  return useSuspenseQuery({
    queryKey: ['activities', 'professional', professionalId, currentActivityId, locale, statusFilter],
    queryFn: async () => {
      const activities = await getActivitiesByProfessional(professionalId, { locale, statusFilter });
      return activities.filter((activity: Activity) => activity.id !== currentActivityId);
    }
  });
};
